import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  artifactService,
  composeArtifactTitle,
  isPrototypeLibraryItem,
  mergeLibraryItems,
  prototyperService,
  type LibraryItem,
  type MessageArtifact,
} from '@ideoz/core';
import { renderArtifactHtml, showToast } from '@ideoz/ui';
import type { ArtifactsOwner } from './artifacts.types';
import { asMessageArtifact } from './artifactMappers';
import { resolveArtifact } from './renderers/registry';

/**
 * Logic for the Artifacts module: load / delete / edit / share artifacts for
 * a given owner, plus the "active artifact" side-panel state. Library rows
 * navigate into the owning conversation: prototypes open the workbench, and
 * documents open the artifact side panel.
 */
export function useArtifacts(owner: ArtifactsOwner) {
  const ownerId = owner.type === 'scope' ? owner.scopeId : owner.startupId;
  const navigate = useNavigate();
  const { startupSlug, scopeId: scopeIdFromUrl } = useParams<{
    startupSlug: string;
    scopeId: string;
  }>();

  const [artifacts, setArtifacts] = useState<LibraryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeArtifact, setActiveArtifact] = useState<MessageArtifact | null>(null);

  const fetchArtifacts = useCallback(async () => {
    if (!ownerId) return;
    try {
      const loadArtifacts =
        owner.type === 'scope'
          ? artifactService.listScopeArtifacts(ownerId)
          : artifactService.listStartupArtifacts(ownerId);
      const loadPrototypes =
        owner.type === 'scope'
          ? prototyperService.listScopePrototypes(ownerId)
          : prototyperService.listStartupPrototypes(ownerId);
      const [artifactsResult, prototypesResult] = await Promise.allSettled([
        loadArtifacts,
        loadPrototypes,
      ]);
      const artifactsList = artifactsResult.status === 'fulfilled' ? artifactsResult.value : [];
      const prototypesList = prototypesResult.status === 'fulfilled' ? prototypesResult.value : [];
      if (artifactsResult.status === 'rejected') {
        console.error('Failed to load artifacts', artifactsResult.reason);
      }
      if (prototypesResult.status === 'rejected') {
        console.error('Failed to load prototypes', prototypesResult.reason);
      }
      setArtifacts(mergeLibraryItems(artifactsList, prototypesList));
    } catch (err) {
      console.error('Failed to load artifacts', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ownerId) return;
      setIsLoading(true);
      try {
        await fetchArtifacts();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerId, fetchArtifacts]);

  const openArtifact = useCallback(
    (row: LibraryItem) => {
      if (isPrototypeLibraryItem(row)) {
        const scopeId = row.scopeId ?? scopeIdFromUrl;
        if (!startupSlug || !scopeId || !row.conversationId) {
          showToast({
            message: 'This prototype is missing a conversation to open.',
            severity: 'error',
          });
          return;
        }
        const params = new URLSearchParams({ mode: 'prototyper', from: 'library' });
        if (row.lastBuiltInteractionId) {
          params.set('restoreInteraction', row.lastBuiltInteractionId);
        }
        navigate(`/${startupSlug}/${scopeId}/${row.conversationId}?${params.toString()}`);
        return;
      }
      const scopeId = row.scopeId ?? scopeIdFromUrl;
      if (!startupSlug || !scopeId || !row.conversationId) {
        showToast({
          message: 'This artifact is missing a conversation to open.',
          severity: 'error',
        });
        return;
      }
      const params = new URLSearchParams({ artifact: row.id });
      if (row.llm_interaction_id) params.set('interaction', row.llm_interaction_id);
      navigate(`/${startupSlug}/${scopeId}/${row.conversationId}?${params.toString()}`);
    },
    [navigate, scopeIdFromUrl, startupSlug]
  );

  const closeArtifact = useCallback(() => setActiveArtifact(null), []);

  const deleteArtifact = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      await artifactService.deleteArtifact(id);
      setArtifacts((prev) => prev.filter((a) => a.id !== id));
      showToast({ message: 'Artifact deleted', severity: 'success' });
    } catch (err) {
      showToast({ message: 'Failed to delete artifact', severity: 'error' });
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  }, []);

  const saveActiveArtifact = useCallback(
    async (content: string) => {
      const id = activeArtifact?.id;
      if (!id) return;
      try {
        const updated = await artifactService.updateArtifact(id, { content });
        // Keep the panel + the row in the list in sync.
        setArtifacts((prev) =>
          prev.map((r) =>
            r.id === id && !isPrototypeLibraryItem(r)
              ? { ...r, content: updated.content ?? content }
              : r
          )
        );
        setActiveArtifact((prev) =>
          prev ? { ...prev, content: updated.content ?? content } : prev
        );
        showToast({ message: 'Artifact updated', severity: 'success' });
      } catch (err) {
        console.error('Failed to update artifact', err);
        showToast({ message: 'Failed to save artifact', severity: 'error' });
        throw err;
      }
    },
    [activeArtifact?.id]
  );

  const saveStructuredActiveArtifact = useCallback(
    async (json: Record<string, unknown>) => {
      const id = activeArtifact?.id;
      if (!id) return;
      try {
        // Manual edit → PUT updates the artifact in place.
        const updated = await artifactService.updateArtifact(id, { structured_content: json });
        const nextStructured = updated.structured_content ?? json;
        // Keep the list row + open panel in sync with the edit.
        setArtifacts((prev) =>
          prev.map((r) =>
            r.id === id && !isPrototypeLibraryItem(r)
              ? { ...r, structured_content: nextStructured, content: updated.content ?? r.content }
              : r
          )
        );
        setActiveArtifact((prev) =>
          prev
            ? {
                ...prev,
                structuredContent: nextStructured,
                content: updated.content ?? prev.content,
              }
            : prev
        );
        showToast({ message: 'Artifact updated', severity: 'success' });
      } catch (err) {
        console.error('Failed to update artifact', err);
        showToast({ message: 'Failed to save artifact', severity: 'error' });
        throw err;
      }
    },
    [activeArtifact?.id]
  );

  const shareActiveArtifact = useCallback(async () => {
    const id = activeArtifact?.id;
    if (!id || !activeArtifact) return;
    try {
      // Predefined types (persona, JTBD, 5 Whys, HMW, Atlas…) publish their
      // designed HTML card via the renderer; generic artifacts (no renderer)
      // publish their markdown rendered to HTML. Both end up as a self-contained
      // HTML document on S3.
      const view = activeArtifact.structuredContent
        ? resolveArtifact(activeArtifact.structuredContent, activeArtifact.name)
        : null;
      const html = view
        ? view.renderer.toHtml(
            view.model,
            composeArtifactTitle(activeArtifact.structuredContent) ?? activeArtifact.name
          )
        : renderArtifactHtml(activeArtifact.content ?? '', activeArtifact.name);
      const updated = await artifactService.publishArtifactToS3(id, html);
      const demoUrl = updated.demo_url ?? activeArtifact.demoUrl ?? null;
      if (!demoUrl) throw new Error('Publish returned no URL');
      setArtifacts((prev) =>
        prev.map((r) =>
          r.id === id && !isPrototypeLibraryItem(r) ? { ...r, demo_url: demoUrl } : r
        )
      );
      setActiveArtifact((prev) => (prev ? { ...prev, demoUrl } : prev));
      await navigator.clipboard.writeText(demoUrl);
      showToast({ message: 'Share link copied to clipboard', severity: 'success' });
    } catch (err) {
      console.error('Failed to share artifact', err);
      showToast({ message: 'Failed to create share link', severity: 'error' });
      throw err;
    }
  }, [activeArtifact]);

  return {
    artifacts,
    isLoading,
    deletingId,
    activeArtifact,
    openArtifact,
    closeArtifact,
    deleteArtifact,
    saveActiveArtifact,
    saveStructuredActiveArtifact,
    shareActiveArtifact,
  };
}
